import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Unlock, KeyRound, Lightbulb, Loader2, DoorOpen, CheckCircle2 } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

// Escape Room secuencial. props:
//  config {intro, success, stages:[{title, prompt, hint}]}
//  onCheck(stageIndex, answer) => Promise<boolean>
//  onComplete(text_answers[])  // respuestas por reto (vacío = rendido)
export default function EscapeRoom({ config, onCheck, onComplete, busy }) {
  const stages = config?.stages || [];
  const [started, setStarted] = useState(!config?.intro);
  const [stage, setStage] = useState(0);
  const [val, setVal] = useState("");
  const [answers, setAnswers] = useState([]);
  const [showHint, setShowHint] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [checking, setChecking] = useState(false);
  const [wrong, setWrong] = useState(false);

  if (!stages.length) return <p className="text-navy/40 text-sm">Este escape room no tiene retos.</p>;

  const advance = (ans) => {
    const na = [...answers, ans];
    if (stage + 1 < stages.length) {
      setAnswers(na); setStage(stage + 1); setVal(""); setShowHint(false); setAttempts(0); setWrong(false);
    } else {
      onComplete(na);
    }
  };

  const check = async () => {
    if (!val.trim()) return;
    setChecking(true); setWrong(false);
    try {
      const ok = await onCheck(stage, val.trim());
      if (ok) advance(val.trim());
      else { setAttempts((a) => a + 1); setWrong(true); }
    } finally { setChecking(false); }
  };

  const skip = () => advance("");

  if (!started) {
    return (
      <div className="text-center py-6" data-testid="escape-intro">
        <div className="w-16 h-16 rounded-2xl bg-navy text-gold flex items-center justify-center mx-auto mb-4"><Lock className="w-8 h-8" /></div>
        <p className="text-navy/70 max-w-md mx-auto leading-relaxed">{config.intro}</p>
        <button data-testid="escape-start" onClick={() => setStarted(true)} className="mt-6 rounded-full bg-gradient-violet text-white px-8 py-3 hover:opacity-90 inline-flex items-center gap-2">
          <DoorOpen className="w-4 h-4" /> Entrar al escape
        </button>
      </div>
    );
  }

  const s = stages[stage];
  return (
    <div data-testid="escape-game">
      <div className="flex items-center gap-1.5 mb-4">
        {stages.map((_, i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i < stage ? "bg-emerald-400" : i === stage ? "bg-violet" : "bg-navy/10"}`} />
        ))}
      </div>
      <AnimatePresence mode="wait">
        <motion.div key={stage} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.35, ease }}
          className="bg-navy text-cream rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-gradient-violet opacity-20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-gold text-xs mb-2"><KeyRound className="w-4 h-4" /> Reto {stage + 1} de {stages.length}</div>
            <h3 className="text-lg font-medium mb-1">{s.title}</h3>
            <p className="text-cream/80 text-sm leading-relaxed mb-4">{s.prompt}</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input data-testid="escape-input" value={val} onChange={(e) => { setVal(e.target.value); setWrong(false); }}
                onKeyDown={(e) => e.key === "Enter" && check()} placeholder="Escribe tu respuesta…"
                className={`flex-1 rounded-xl bg-cream/10 border px-4 py-3 text-sm text-cream placeholder:text-cream/40 outline-none focus:border-gold ${wrong ? "border-rose-400" : "border-cream/20"}`} />
              <button data-testid="escape-check" onClick={check} disabled={checking || busy || !val.trim()}
                className="rounded-xl bg-gold text-navy font-medium px-5 py-3 text-sm hover:opacity-90 disabled:opacity-40 inline-flex items-center justify-center gap-2">
                {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Unlock className="w-4 h-4" />} Abrir
              </button>
            </div>
            {wrong && <p className="text-rose-300 text-xs mt-2" data-testid="escape-wrong">Respuesta incorrecta, intenta de nuevo.</p>}
            <div className="flex items-center gap-4 mt-3">
              {s.hint && <button data-testid="escape-hint-btn" onClick={() => setShowHint((h) => !h)} className="text-cream/60 hover:text-gold text-xs inline-flex items-center gap-1.5"><Lightbulb className="w-3.5 h-3.5" /> {showHint ? "Ocultar pista" : "Pista"}</button>}
              {attempts >= 2 && <button data-testid="escape-skip" onClick={skip} className="text-cream/50 hover:text-cream/80 text-xs">Rendirse en este reto →</button>}
            </div>
            {showHint && s.hint && <p className="text-gold/90 text-xs mt-2 bg-cream/5 rounded-lg px-3 py-2" data-testid="escape-hint">💡 {s.hint}</p>}
          </div>
        </motion.div>
      </AnimatePresence>
      <div className="flex items-center gap-1.5 mt-3 text-xs text-navy/40">
        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {stage} de {stages.length} retos resueltos
      </div>
    </div>
  );
}
